// Main App component
// This handles routing (which page to show) and tracks if user is logged in

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

// Import all our pages
import HeroPage from './pages/HeroPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import OnboardingPage from './pages/OnboardingPage';
import CreateFamilyPage from './pages/CreateFamilyPage';
import JoinFamilyPage from './pages/JoinFamilyPage';
import TodoListPage from './pages/TodoListPage';
import DiscoverPage from './pages/DiscoverPage';
import SettingsPage from './pages/SettingsPage';
import NotificationsPage from './pages/NotificationsPage';
import AdminPage from './pages/AdminPage';

// Import admin components
import { AdminProvider } from './contexts/AdminContext';
import AdminBanner from './components/AdminBanner';

// Import PostHog provider
import { PostHogProvider } from './contexts/PostHogProvider';

// Import styles
import './App.css';

function App() {
  // Track the current user (null if not logged in)
  const [user, setUser] = useState(null);

  // Track user's profile data from Firestore (includes familyId)
  const [userProfile, setUserProfile] = useState(null);

  // Track if we're still checking if user is logged in
  const [loading, setLoading] = useState(true);

  // This runs once when the app starts
  // It listens for changes to the login state
  useEffect(() => {
    // Track if component is still mounted to avoid state updates after unmount
    let isMounted = true;
    // Track the current request to ignore stale results
    let currentRequestId = 0;

    // onAuthStateChanged is a Firebase function that calls our callback
    // whenever the user logs in or out
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (!isMounted) return;

        // Increment request ID to invalidate any previous requests
        const requestId = ++currentRequestId;

        setUser(currentUser);

        if (currentUser) {
          // User is logged in, fetch their profile from Firestore
          try {
            const userDocRef = doc(db, 'users', currentUser.uid);
            const userDoc = await getDoc(userDocRef);

            // Only process if this is still the current request and component is mounted
            if (!isMounted || requestId !== currentRequestId) return;

            if (userDoc.exists()) {
              setUserProfile(userDoc.data());
            } else {
              // User exists in Auth but not in Firestore yet
              // This happens right after signup before profile is created
              setUserProfile(null);
            }
          } catch (error) {
            // Only process if this is still the current request and component is mounted
            if (!isMounted || requestId !== currentRequestId) return;
            
            // Ignore abort errors - happens when component unmounts or new request starts
            const errorMessage = error?.message || '';
            const errorName = error?.name || '';
            if (
              errorName === 'AbortError' || 
              errorMessage.includes('aborted') ||
              errorMessage.includes('user aborted') ||
              errorMessage.includes('The user aborted')
            ) {
              return;
            }
            console.error('Error fetching user profile:', error);
            setUserProfile(null);
          }
        } else {
          // User is logged out
          setUserProfile(null);
        }

        // Only update loading state if this is still the current request
        if (isMounted && requestId === currentRequestId) {
          setLoading(false);
        }
      } catch (error) {
        // Catch any unexpected errors in the auth callback
        const errorMessage = error?.message || '';
        const errorName = error?.name || '';
        if (
          errorName === 'AbortError' || 
          errorMessage.includes('aborted') ||
          errorMessage.includes('user aborted') ||
          errorMessage.includes('The user aborted')
        ) {
          return;
        }
        console.error('Unexpected error in auth state change:', error);
        if (isMounted) {
          setLoading(false);
        }
      }
    });

    // Cleanup: stop listening when component unmounts
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Function to refresh user profile (called after joining/creating family)
  const refreshUserProfile = async () => {
    if (user) {
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          setUserProfile(userDoc.data());
        }
      } catch (error) {
        // Ignore abort errors
        const errorMessage = error?.message || '';
        const errorName = error?.name || '';
        if (
          errorName === 'AbortError' || 
          errorMessage.includes('aborted') ||
          errorMessage.includes('user aborted') ||
          errorMessage.includes('The user aborted')
        ) {
          return;
        }
        console.error('Error refreshing user profile:', error);
      }
    }
  };

  // Show loading screen while checking auth state
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <PostHogProvider user={user} userProfile={userProfile}>
        <AdminProvider userId={user?.uid}>
          <AdminBanner />
          <div className="app">
          <Routes>
          {/* Login page - redirect to appropriate page if already logged in */}
          <Route
            path="/login"
            element={
              user
                ? <Navigate to={userProfile?.onboardingComplete ? (userProfile?.familyId ? "/todos" : "/family-setup") : "/onboarding"} />
                : <LoginPage />
            }
          />

          {/* Signup page - redirect if already logged in */}
          <Route
            path="/signup"
            element={
              user
                ? <Navigate to={userProfile?.onboardingComplete ? (userProfile?.familyId ? "/todos" : "/family-setup") : "/onboarding"} />
                : <SignupPage />
            }
          />

          {/* Onboarding page - collect additional info after signup */}
          <Route
            path="/onboarding"
            element={
              !user
                ? <Navigate to="/login" />
                : userProfile?.onboardingComplete
                  ? <Navigate to={userProfile?.familyId ? "/todos" : "/family-setup"} />
                  : <OnboardingPage user={user} onOnboardingComplete={refreshUserProfile} />
            }
          />

          {/* Family setup - choose to create or join */}
          <Route
            path="/family-setup"
            element={
              !user
                ? <Navigate to="/login" />
                : !userProfile?.onboardingComplete
                  ? <Navigate to="/onboarding" />
                  : userProfile?.familyId
                    ? <Navigate to="/todos" />
                  : (
                    <div className="family-setup-choice">
                      <h1>Set up your family!</h1>
                      <p>Has your partner signed up yet?</p>
                      <div className="setup-buttons">
                        <a href="/create-family" className="button button-primary">
                          Create a new family
                        </a>
                        <a href="/join-family" className="button button-secondary">
                          Join existing family
                        </a>
                      </div>
                    </div>
                  )
            }
          />

          {/* Create family page */}
          <Route
            path="/create-family"
            element={
              !user
                ? <Navigate to="/login" />
                : !userProfile?.onboardingComplete
                  ? <Navigate to="/onboarding" />
                  : userProfile?.familyId
                    ? <Navigate to="/todos" />
                    : <CreateFamilyPage user={user} userProfile={userProfile} onFamilyCreated={refreshUserProfile} />
            }
          />

          {/* Join family page */}
          <Route
            path="/join-family"
            element={
              !user
                ? <Navigate to="/login" />
                : !userProfile?.onboardingComplete
                  ? <Navigate to="/onboarding" />
                  : userProfile?.familyId
                    ? <Navigate to="/todos" />
                    : <JoinFamilyPage user={user} userProfile={userProfile} onFamilyJoined={refreshUserProfile} />
            }
          />

          {/* Main todo list page (Home) */}
          <Route
            path="/todos"
            element={
              !user
                ? <Navigate to="/login" />
                : !userProfile?.onboardingComplete
                  ? <Navigate to="/onboarding" />
                  : !userProfile?.familyId
                    ? <Navigate to="/family-setup" />
                    : <TodoListPage user={user} userProfile={userProfile} />
            }
          />

          {/* Discover page */}
          <Route
            path="/discover"
            element={
              !user
                ? <Navigate to="/login" />
                : !userProfile?.onboardingComplete
                  ? <Navigate to="/onboarding" />
                  : !userProfile?.familyId
                    ? <Navigate to="/family-setup" />
                    : <DiscoverPage user={user} userProfile={userProfile} />
            }
          />

          {/* Settings page */}
          <Route
            path="/settings"
            element={
              !user
                ? <Navigate to="/login" />
                : !userProfile?.onboardingComplete
                  ? <Navigate to="/onboarding" />
                  : !userProfile?.familyId
                    ? <Navigate to="/family-setup" />
                    : <SettingsPage user={user} userProfile={userProfile} onSettingsSaved={refreshUserProfile} />
            }
          />

          {/* Notifications page */}
          <Route
            path="/notifications"
            element={
              !user
                ? <Navigate to="/login" />
                : !userProfile?.onboardingComplete
                  ? <Navigate to="/onboarding" />
                  : !userProfile?.familyId
                    ? <Navigate to="/family-setup" />
                    : <NotificationsPage user={user} userProfile={userProfile} />
            }
          />

          {/* Admin page - only accessible to logged in users (admin check happens in component) */}
          <Route
            path="/admin"
            element={
              !user
                ? <Navigate to="/login" />
                : !userProfile?.onboardingComplete
                  ? <Navigate to="/onboarding" />
                  : !userProfile?.familyId
                    ? <Navigate to="/family-setup" />
                    : <AdminPage user={user} userProfile={userProfile} />
            }
          />

          {/* Default route - show hero page if not logged in, otherwise redirect based on user state */}
          <Route
            path="/"
            element={
              !user
                ? <HeroPage />
                : !userProfile?.onboardingComplete
                  ? <Navigate to="/onboarding" />
                  : userProfile?.familyId
                    ? <Navigate to="/todos" />
                    : <Navigate to="/family-setup" />
            }
          />
          </Routes>
        </div>
      </AdminProvider>
      </PostHogProvider>
    </BrowserRouter>
  );
}

export default App;
